import React, { useState, useEffect } from 'react';
import { Search, UserPlus, Pencil } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";

import {
  useUsers,
  useCreateUser,
  useUpdateUser,
  useResetUserPassword,
  useUnlockUserAccount,
  type User as ApiUser,
  type CreateUserData,
  type UpdateUserData,
  type UserQueryParams
} from '@/hooks/user-hooks';

import { AddUserForm, EditUserForm } from '../components';
import { useDensity } from '@/components/admin/context/DensityContext';

// Extended User interface for the module's specific needs
interface ExtendedUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  role: string;
  date_joined: string;
  last_login?: string;
  user_type?: 'student' | 'faculty' | 'staff' | 'librarian' | 'admin' | 'guest';
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  is_active_member?: boolean;
  membership_expiry?: string;
  created_at?: string;
  updated_at?: string;
}

interface AddUserFormData extends CreateUserData {
  user_type: 'student' | 'faculty' | 'staff' | 'librarian' | 'admin' | 'guest';
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  is_active_member?: boolean;
  membership_expiry?: string;
}

interface EditUserFormData extends UpdateUserData {
  user_type: 'student' | 'faculty' | 'staff' | 'librarian' | 'admin' | 'guest';
  student_id?: string;
  employee_id?: string;
  phone_number?: string;
  date_of_birth?: string;
  address?: string;
  is_active_member?: boolean;
  membership_expiry?: string;
  password?: string;
  confirm_password?: string;
}

const UsersModule: React.FC = () => {
  const { toast } = useToast();
  const { density } = useDensity();

  // Server-side query parameters
  const [queryParams, setQueryParams] = useState<UserQueryParams>({
    page: 1,
    page_size: 25,
    ordering: 'username'
  });
  const [searchInput, setSearchInput] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // UI state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<ExtendedUser | null>(null);
  const [editingErrors, setEditingErrors] = useState<Record<string, string>>({});

  // React Query hooks
  const { data: usersResponse, isLoading, refetch } = useUsers(queryParams);
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const resetPasswordMutation = useResetUserPassword();
  const unlockAccountMutation = useUnlockUserAccount();

  // Extract users from response
  const users = usersResponse?.results || [];
  const totalUsers = usersResponse?.count ?? users.length;

  const currentPage = queryParams.page ?? 1;
  const pageSize = queryParams.page_size ?? 25;
  const totalPages = Math.max(1, Math.ceil(totalUsers / pageSize));

  const updateQueryParams = (updates: Partial<UserQueryParams>) => {
    setQueryParams(prev => ({
      ...prev,
      ...updates,
      page: updates.page ?? 1
    }));
  };

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      const val = searchInput.trim();
      updateQueryParams({ search: val || undefined, page: 1 });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Apply role & status filters
  useEffect(() => {
    const updates: Partial<UserQueryParams> = {};
    if (filterRole !== 'all') {
      (updates as any).user_type = filterRole;
    } else {
      (updates as any).user_type = undefined;
    }
    if (filterStatus !== 'all') {
      updates.is_active = filterStatus === 'active';
    } else {
      updates.is_active = undefined;
    }
    updateQueryParams(updates);
  }, [filterRole, filterStatus]);

  const handleAddUser = async (formData: AddUserFormData) => {
    try {
      await createUserMutation.mutateAsync(formData);
      setShowAddDialog(false);
      refetch();
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleEditUser = async (formData: EditUserFormData) => {
    if (!editingUser) return;
    setEditingErrors({});
    try {
      await updateUserMutation.mutateAsync({
        id: editingUser.id,
        userData: formData
      });
      setShowEditDialog(false);
      setEditingUser(null);
      refetch();
    } catch (error) {
      const axiosErr: any = error;
      const data = axiosErr?.response?.data;
      const fieldErrs: Record<string, string> = {};
      const source = data?.errors && typeof data.errors === 'object' ? data.errors : data;
      if (source && typeof source === 'object') {
        Object.entries(source).forEach(([k, v]) => {
          if (Array.isArray(v) && v.length) fieldErrs[k] = String(v[0]);
        });
      }
      if (Object.keys(fieldErrs).length) {
        setEditingErrors(fieldErrs);
      }
      console.error('Failed to update user:', error);
    }
  };

  const handleResetPassword = async (userId: number) => {
    try {
      const result = await resetPasswordMutation.mutateAsync(userId);
      toast({
        title: "Success",
        description: `Password reset. A temporary password was e-mailed to ${result.delivered_to}.`,
      });
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  const handleUnlockAccount = async (userId: number) => {
    try {
      await unlockAccountMutation.mutateAsync(userId);
    } catch (error) {
      // Error handled by mutation hook
    }
  };

  const handleCloseAddDialog = () => setShowAddDialog(false);
  const handleCloseEditDialog = () => {
    setShowEditDialog(false);
    setEditingUser(null);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-500">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-[1400px]">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-1">Manage librarians and students.</p>
        </div>
        <button
          onClick={() => setShowAddDialog(true)}
          className="bg-amber-500 hover:bg-amber-600 text-white font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
        >
          <UserPlus className="h-4 w-4" />
          Invite User
        </button>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: totalUsers.toLocaleString() },
          { label: 'Students', value: users.filter(u => (u as any).user_type === 'student').length.toLocaleString() },
          { label: 'Faculty / Staff', value: users.filter(u => ['faculty', 'staff', 'librarian'].includes((u as any).user_type)).length.toLocaleString() },
          { label: 'Active', value: users.filter(u => u.is_active).length.toLocaleString() },
        ].map(s => (
          <div key={s.label} className="bg-white border border-[#E8E4DC] rounded-xl px-5 py-4">
            <div className="text-2xl font-bold text-gray-900">{s.value}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-gray-400 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search users..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
          />
        </div>
        <span className="text-xs text-gray-400 font-mono ml-auto">{totalUsers} records</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E8E4DC] rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F8F7F5] border-b border-[#E8E4DC]">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Name</th>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Role</th>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Status</th>
              <th className="text-left px-5 py-3 text-xs font-mono uppercase tracking-wider text-gray-400">Joined</th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F4F6]">
            {users.map(user => (
              <tr key={user.id} className="hover:bg-[#FFFBEB] transition-colors group">
                <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                  <div className="text-sm font-medium text-gray-900">
                    {user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
                  </div>
                  <div className="text-xs text-gray-400">{user.email}</div>
                </td>
                <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                    {(user as any).user_type ?? (user as any).role ?? '—'}
                  </span>
                </td>
                <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    user.is_active
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-red-50 text-red-600 border-red-200'
                  }`}>
                    {user.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'} text-sm text-gray-500`}>
                  {user.date_joined ? new Date(user.date_joined).toLocaleDateString() : '—'}
                </td>
                <td className={`px-5 ${density === 'comfortable' ? 'py-4' : 'py-2'}`}>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity justify-end">
                    <button
                      onClick={() => { setEditingUser(user as ExtendedUser); setShowEditDialog(true); }}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="px-5 py-3 border-t border-[#E8E4DC] flex items-center justify-between">
          <span className="text-xs text-gray-400 font-mono">Page {currentPage} of {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => updateQueryParams({ page: Math.max(1, currentPage - 1) })}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => updateQueryParams({ page: Math.min(totalPages, currentPage + 1) })}
              disabled={currentPage >= totalPages}
              className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Drawers */}
      <AddUserForm
        open={showAddDialog}
        onClose={handleCloseAddDialog}
        onSubmit={handleAddUser}
        onCancel={handleCloseAddDialog}
        isLoading={createUserMutation.isPending}
      />
      <EditUserForm
        open={showEditDialog}
        onClose={handleCloseEditDialog}
        user={editingUser as any}
        onSubmit={handleEditUser}
        onCancel={handleCloseEditDialog}
        isLoading={updateUserMutation.isPending}
        errors={editingErrors}
      />
    </div>
  );
};

export default UsersModule;
